import { api } from '../api/client';
import { CRM_FRAME_PATH } from './crmBridge';

/** An account as the CRM lists it for this login (its /v1/accounts). */
export interface CrmAccount {
  account_id: string;
  profile_name: string;
  username: string;
  avatar_url: string;
  platform: string;
  is_enabled: boolean;
  /** Not on a plan (CONTEXT.md): listed, but every CRM page is refused (No-plan lock).
   *  Absent from a gate remembered before the guard: read as unlocked until the CRM answers. */
  plan_locked?: boolean;
}

/**
 * The CRM gate for this login (ADR 0019: CRM access is independent of web
 * console access and derived from CRM grants). 'none' means the person has
 * no CRM profile or no CRM accounts, so the shell hides the CRM view;
 * 'error' means the CRM could not be reached and the view is unavailable.
 */
export type CrmAccess =
  | { state: 'loading' }
  | { state: 'none' }
  | { state: 'error' }
  | { state: 'ok'; accounts: CrmAccount[] };

async function listAccounts(token: string, signal?: AbortSignal): Promise<Response> {
  return fetch(`${CRM_FRAME_PATH}v1/accounts`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    cache: 'no-store',
    signal,
  });
}

/** Ask the CRM itself which accounts this login may work: its gate is the truth. */
export async function fetchCrmAccess(token: string, signal?: AbortSignal): Promise<CrmAccess> {
  try {
    let res = await listAccounts(token, signal);
    if (res.status === 401) {
      // The console's token may just be stale; the CRM verifies the same Supabase JWT.
      const fresh = await api.refreshAccessToken();
      if (!fresh) return { state: 'none' };
      res = await listAccounts(fresh, signal);
    }
    if (res.status === 401 || res.status === 403 || res.status === 404) return { state: 'none' };
    if (!res.ok) return { state: 'error' };
    const body = (await res.json().catch(() => null)) as { data?: { list?: unknown }; list?: unknown } | null;
    const raw = body?.data?.list ?? body?.list;
    const accounts: CrmAccount[] = Array.isArray(raw)
      ? raw
          .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object' && typeof (a as Record<string, unknown>).account_id === 'string')
          .map((a) => ({
            account_id: String(a.account_id),
            profile_name: String(a.profile_name ?? ''),
            username: String(a.username ?? ''),
            avatar_url: String(a.avatar_url ?? ''),
            platform: String(a.platform ?? ''),
            is_enabled: a.is_enabled !== false,
            plan_locked: a.plan_locked === true,
          }))
      : [];
    return accounts.length ? { state: 'ok', accounts } : { state: 'none' };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') return { state: 'loading' };
    return { state: 'error' };
  }
}

const REMEMBERED_KEY = 'crm_access_remembered';

/**
 * The last working gate for this login, so a reload can open a React CRM page
 * at once while the CRM is asked again in the background (ADR 0027 speed
 * work). Every CRM call is still checked by the CRM itself.
 */
export function rememberedCrmAccess(userId: string): CrmAccess | null {
  try {
    const raw = JSON.parse(localStorage.getItem(REMEMBERED_KEY) || 'null') as {
      userId?: string;
      accounts?: CrmAccount[];
    } | null;
    return raw?.userId === userId &&
      Array.isArray(raw.accounts) &&
      raw.accounts.length
      ? { state: 'ok', accounts: raw.accounts }
      : null;
  } catch {
    return null;
  }
}

/** Keep a working answer; 'none' forgets it; an unreachable CRM keeps the last good one. */
export function rememberCrmAccess(userId: string, access: CrmAccess) {
  try {
    if (access.state === 'ok')
      localStorage.setItem(
        REMEMBERED_KEY,
        JSON.stringify({ userId, accounts: access.accounts }),
      );
    else if (access.state === 'none') localStorage.removeItem(REMEMBERED_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** Logout. */
export function forgetCrmAccess() {
  try {
    localStorage.removeItem(REMEMBERED_KEY);
  } catch {
    /* storage unavailable */
  }
}
